import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  distinctUntilChanged,
  map,
  merge,
  Observable,
  shareReplay,
  skip,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import {
  TuiButtonModule,
  TuiGroupModule,
  TuiHintModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { getCalculatedQuantity } from '@shared/utils/cargo-calculation';
import { getCargoPlaceQuantityByTrailerType } from '@shared/constants/vehicles';
import { AsyncPipe } from '@angular/common';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { TuiInputNumberModule } from '@taiga-ui/kit';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { CommonStateService } from '../../../../services/common-state.service';
import { TrailerType } from '../../../../../../types/trailer';
import { PreOrderCargoPlaceDetailsForm } from '../../../../../../types/pre-order';

@Component({
  selector: 'app-cargo-place-details',
  standalone: true,
  imports: [
    AsyncPipe,
    AutocompleteComponent,
    TuiGroupModule,
    TuiInputNumberModule,
    CheckboxComponent,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    TuiHintModule,
    TuiButtonModule,
  ],
  templateUrl: './cargo-place-details.component.html',
  styleUrl: './cargo-place-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CargoPlaceDetailsComponent implements OnInit {
  @Output() remove = new EventEmitter<void>();
  @Output() create = new EventEmitter<void>();

  @Input({ required: true })
  cargoPlaceDetailsForm: FormGroup<PreOrderCargoPlaceDetailsForm>;

  @Input({ required: true }) isImport: boolean;
  @Input({ required: true }) last: boolean;
  hasBox$: Observable<boolean>;

  preparedPackageTypes$ = this.catalogs.packageTypes$.pipe(
    map(packageTypes =>
      packageTypes.map(packageType => ({
        ...packageType,
        itemText: `${packageType.code} - ${packageType.name}`,
      })),
    ),
  );

  cargoPlaceQuantityLimit = this.fb.control<Nullable<number>>({
    value: null,
    disabled: true,
  });

  constructor(
    protected readonly catalogs: CargoCatalogsService,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly commonState: CommonStateService,
  ) {}

  onCargoPlaceTypeChange() {
    this.cargoPlaceJson.controls.code.valueChanges
      .pipe(
        withLatestFrom(this.commonState.trailerType$),
        tap(([type, trailerType]) => {
          this.updateCargoPlaceDetails(type, trailerType as TrailerType);
          this.clearPackageDetails();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onPackageTypeChange() {
    this.cargoPlaceDetailsForm.controls.packageTypeJson.controls.code.valueChanges
      .pipe(
        tap(packageType => {
          this.updatePackageDetails(packageType);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onHasBoxChange() {
    this.hasBox$
      .pipe(
        skip(1),
        distinctUntilChanged(),
        tap(hasBox => {
          if (!hasBox) {
            this.cargoPlaceDetailsForm.controls.packageTypeJson.reset();
            this.cargoPlaceDetailsForm.controls.packageQuantity.reset();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onTrailerTypeChange() {
    this.commonState.trailerType$
      .pipe(
        skip(1),
        tap(trailerType => {
          const cargoPlaceType = this.cargoPlaceJson.controls.code.value ?? '';
          const packageType = this.packageTypeJson.controls.code.value ?? '';
          this.updateCargoPlaceDetails(
            cargoPlaceType,
            trailerType as TrailerType,
          );
          this.updatePackageDetails(packageType);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  clearPackageDetails() {
    this.packageTypeJson.reset();
    this.cargoPlaceDetailsForm.patchValue({
      packageQuantityLimit: null,
      packageQuantity: null,
    });
  }

  updateCargoPlaceDetails(
    cargoPlaceId: Nullable<string>,
    trailerType: TrailerType,
  ) {
    const cargoPlaceDetails = this.catalogs.cargoPlaceMap.get(
      cargoPlaceId || '',
    );
    if (cargoPlaceDetails) {
      const limit = getCargoPlaceQuantityByTrailerType(
        trailerType,
        cargoPlaceDetails.quantityInTrailerJson,
      );
      this.cargoPlaceJson.patchValue(cargoPlaceDetails, { emitEvent: false });
      this.cargoPlaceQuantityLimit.setValue(limit);
    } else {
      this.cargoPlaceJson.reset(undefined, { emitEvent: false });
      this.cargoPlaceJson.markAsDirty();
      this.cargoPlaceQuantityLimit.setValue(null);
    }
  }

  calculatePlaceQuantity() {
    merge(
      this.cargoPlaceDetailsForm.controls.packageQuantityLimit.valueChanges,
      this.cargoPlaceDetailsForm.controls.packageQuantity.valueChanges,
    )
      .pipe(
        map(() => {
          const { packageQuantityLimit = 0, packageQuantity = 0 } =
            this.cargoPlaceDetailsForm.getRawValue();
          return [packageQuantityLimit, packageQuantity];
        }),
        tap(([packageQuantityLimit, packageQuantity]) => {
          this.cargoPlaceDetailsForm.controls.cargoPlaceQuantity.setValue(
            getCalculatedQuantity(packageQuantityLimit, packageQuantity),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updatePackageDetails(packageTypeCode: Nullable<string>) {
    const packageDetails = this.catalogs.packageTypesMap.get(
      packageTypeCode || '',
    );
    if (packageDetails) {
      this.cargoPlaceDetailsForm.controls.packageTypeJson.patchValue(
        packageDetails,
        {
          emitEvent: false,
        },
      );
    } else {
      this.cargoPlaceDetailsForm.controls.packageTypeJson.reset(
        {},
        { emitEvent: false },
      );
      this.cargoPlaceDetailsForm.controls.packageTypeJson.markAsDirty();
    }
    const packageCargoPlace = packageDetails?.cargoPlaceQuantityJson.find(
      place => place.cargoPlaceId === this.cargoPlaceJson.value.id,
    );
    this.cargoPlaceDetailsForm.controls.packageQuantityLimit.setValue(
      packageCargoPlace?.quantity ?? 0,
    );
  }

  get cargoPlaceJson() {
    return this.cargoPlaceDetailsForm.controls.cargoPlaceJson;
  }

  get packageTypeJson() {
    return this.cargoPlaceDetailsForm.controls.packageTypeJson;
  }

  setCargoPlaceLimit() {
    const cargoPlaceJson = this.cargoPlaceJson.getRawValue();
    const trailerType = (this.commonState.trailerType$.value ??
      '') as TrailerType;
    const limit = getCargoPlaceQuantityByTrailerType(
      trailerType,
      cargoPlaceJson.quantityInTrailerJson,
    );

    this.cargoPlaceQuantityLimit.setValue(limit);
  }

  ngOnInit() {
    this.hasBox$ = this.cargoPlaceDetailsForm.controls.box.valueChanges.pipe(
      startWith(this.cargoPlaceDetailsForm.controls.box.getRawValue()),
      map(value => value ?? false),
      takeUntilDestroyed(this.destroyRef),
      shareReplay(),
    );
    this.onTrailerTypeChange();
    this.onPackageTypeChange();
    this.calculatePlaceQuantity();
    this.onHasBoxChange();
    this.onCargoPlaceTypeChange();
    this.setCargoPlaceLimit();
    this.onHasBoxChange();
  }
}
